import { useEffect, useMemo, useRef, useState } from 'react';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  initOAuthConnection,
  openOAuthWindow,
  pollForOAuthConnection,
} from '@/lib/oauth';
import type {
  OAuthDisconnectResponse,
  OAuthProviderReadiness,
  OAuthProviderStatusResponse,
} from '@shared/types/integrations';
import { Link } from 'react-router-dom';

interface Integration {
  id: string;
  provider: string;
  display_name: string | null;
  is_active: boolean;
  scopes: string[] | null;
  token_expires_at: string | null;
  created_at: string;
  config: Record<string, unknown> | null;
}

const SCOPE_LABELS: Record<string, string> = {
  'https://www.googleapis.com/auth/gmail.send': 'Envoi',
  'https://www.googleapis.com/auth/gmail.compose': 'Brouillons',
  'https://www.googleapis.com/auth/gmail.metadata': 'Métadonnées threads',
  'https://www.googleapis.com/auth/gmail.readonly': 'Lecture des e-mails',
  'https://www.googleapis.com/auth/drive.readonly': 'Lecture',
  'https://www.googleapis.com/auth/drive.file': 'Fichiers créés par Domino',
  'https://www.googleapis.com/auth/drive': 'Lecture / écriture',
  'https://www.googleapis.com/auth/documents.readonly': 'Docs (lecture)',
  'https://www.googleapis.com/auth/spreadsheets': 'Sheets (édition)',
  'https://www.googleapis.com/auth/spreadsheets.readonly': 'Sheets (lecture)',
  'https://www.googleapis.com/auth/calendar.readonly': 'Calendrier (lecture)',
  'https://www.googleapis.com/auth/calendar.events': 'Calendrier (événements)',
  'sales-email-read': 'E-mails consignés (lecture)',
};

const REQUIRED_PROVIDER_SCOPES: Partial<Record<string, string[]>> = {
  gmail: [
    'https://www.googleapis.com/auth/gmail.send',
    'https://www.googleapis.com/auth/gmail.compose',
    'https://www.googleapis.com/auth/gmail.readonly',
  ],
  google_workspace: [
    'https://www.googleapis.com/auth/drive.readonly',
    'https://www.googleapis.com/auth/drive.file',
    'https://www.googleapis.com/auth/spreadsheets',
  ],
  google_calendar: [
    'https://www.googleapis.com/auth/calendar.readonly',
    'https://www.googleapis.com/auth/calendar.events',
  ],
  hubspot: ['sales-email-read'],
};

const formatScopes = (scopes: string[]): string => {
  if (!scopes || scopes.length === 0) return '';
  const labels = scopes.map((s) => SCOPE_LABELS[s] ?? s.split('/').pop() ?? s);
  const unique = [...new Set(labels)];
  return unique.join(', ');
};

const PROVIDER_META: Record<string, { label: string; description: string }> = {
  hubspot: { label: 'HubSpot', description: 'Données CRM utilisées dans vos travaux' },
  gmail: { label: 'Gmail', description: 'Envoyer des emails et suivre les réponses des threads Domino' },
  google_workspace: { label: 'Google Drive', description: 'Lire et chercher des fichiers' },
  google_calendar: { label: 'Google Calendar', description: 'Lister et créer des événements' },
};

export const ConnectionsPage = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [oauthProgress, setOAuthProgress] = useState<{
    provider: string;
    phase: 'starting' | 'waiting';
    elapsedMs: number;
  } | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState<string | null>(null);
  const [providerErrors, setProviderErrors] = useState<Record<string, string>>({});
  const [providerReadiness, setProviderReadiness] = useState<Record<string, OAuthProviderReadiness>>({});
  const assistantContext = useMemo(() => ({
    surface: 'home' as const, routePath: '/connections', routeLabel: 'Connexions',
    visibleSummary: loading ? 'Connexions en cours de chargement.'
      : `Outils connectés : ${integrations.filter((item) => item.is_active).map((item) => item.provider).join(', ') || 'aucun'}.`,
  }), [integrations, loading]);
  useRegisterAppAssistantContext(assistantContext);
  const oauthPollAbortRef = useRef<AbortController | null>(null);

  const fetchIntegrations = async () => {
    const { data } = await supabase
      .from('integrations')
      .select('id, provider, display_name, is_active, scopes, token_expires_at, created_at, config')
      .order('created_at', { ascending: false });

    if (data) {
      setIntegrations(data as Integration[]);
    }
    setLoading(false);
  };

  const fetchOAuthProviderStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('oauth-provider-status', {
        body: { providers: Object.keys(PROVIDER_META) },
      });
      if (error) {
        console.warn('Failed to fetch OAuth provider status', error);
        return;
      }

      const response = (data ?? null) as OAuthProviderStatusResponse | null;
      if (response?.success && response.providers) {
        setProviderReadiness(response.providers);
      }
    } catch (error) {
      console.warn('Failed to fetch OAuth provider status', error);
    }
  };

  useEffect(() => {
    void fetchIntegrations();
    void fetchOAuthProviderStatus();

    return () => {
      oauthPollAbortRef.current?.abort();
      oauthPollAbortRef.current = null;
    };
  }, []);

  const clearProviderError = (provider: string) => {
    setProviderErrors((prev) => {
      if (!prev[provider]) return prev;
      const next = { ...prev };
      delete next[provider];
      return next;
    });
  };

  const handleConnect = async (provider: string) => {
    clearProviderError(provider);
    setOAuthProgress({ provider, phase: 'starting', elapsedMs: 0 });

    try {
      const { url, attemptId } = await initOAuthConnection(provider);
      const windowResult = openOAuthWindow(url);

      if (!windowResult.shouldPoll) {
        return;
      }

      setOAuthProgress({ provider, phase: 'waiting', elapsedMs: 0 });
      oauthPollAbortRef.current?.abort();
      const abortController = new AbortController();
      oauthPollAbortRef.current = abortController;

      const pollResult = await pollForOAuthConnection({
        provider,
        attemptId,
        signal: abortController.signal,
        onTick: ({ elapsedMs }) => {
          setOAuthProgress((current) =>
            current?.provider === provider
              ? { provider, phase: 'waiting', elapsedMs }
              : current
          );
        },
      });

      if (oauthPollAbortRef.current === abortController) {
        oauthPollAbortRef.current = null;
      }

      if (pollResult.connected) {
        await fetchIntegrations();
        return;
      }

      if (pollResult.aborted) {
        return;
      }

      if (pollResult.timedOut) {
        setProviderErrors((prev) => ({
          ...prev,
          [provider]: 'Autorisation non terminée à temps. Réessayez la connexion.',
        }));
      }
    } catch (error) {
      const payload = (error as { payload?: unknown })?.payload;
      console.error('Failed to start OAuth connection', {
        provider,
        error: error instanceof Error ? error.message : String(error),
        payloadShape: payload && typeof payload === 'object' ? Object.keys(payload as Record<string, unknown>) : payload,
      });
      setProviderErrors((prev) => ({
        ...prev,
        [provider]: error instanceof Error ? error.message : 'Erreur lors de la connexion OAuth',
      }));
    } finally {
      setOAuthProgress((current) => (current?.provider === provider ? null : current));
    }
  };

  const handleDisconnect = async (provider: string) => {
    clearProviderError(provider);
    setDisconnectingProvider(provider);

    try {
      const { data, error } = await supabase.functions.invoke('oauth-disconnect', {
        body: { provider },
      });

      if (error) {
        throw new Error(error.message || `Failed to disconnect ${provider}`);
      }

      const response = (data ?? null) as OAuthDisconnectResponse | null;
      if (!response?.success) {
        throw new Error(response?.error || `Failed to disconnect ${provider}`);
      }

      await fetchIntegrations();
    } catch (error) {
      console.error('Failed to disconnect integration', {
        provider,
        error: error instanceof Error ? error.message : String(error),
      });
      setProviderErrors((prev) => ({
        ...prev,
        [provider]: error instanceof Error ? error.message : 'Erreur lors de la déconnexion',
      }));
    } finally {
      setDisconnectingProvider((current) => (current === provider ? null : current));
    }
  };

  const connectedProviders = new Set(integrations.filter(i => i.is_active).map(i => i.provider));

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold text-gray-900">Connexions</h1>
        <p className="text-sm text-gray-500 mt-1">Choisissez les services que les applications Domino peuvent consulter ou utiliser.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-900 rounded-full" />
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(PROVIDER_META).map(([provider, meta]) => {
            const isConnected = connectedProviders.has(provider);
            const integration = integrations.find(i => i.provider === provider && i.is_active);
            const requiredScopes = REQUIRED_PROVIDER_SCOPES[provider] ?? [];
            const grantedScopes = Array.isArray(integration?.scopes) ? integration.scopes : null;
            const missingScopes = integration
              ? (
                grantedScopes
                  ? requiredScopes.filter((scope) => !grantedScopes.includes(scope))
                  : requiredScopes
              )
              : [];
            const hasScopeDrift = missingScopes.length > 0;
            const isOAuthBusy = oauthProgress?.provider === provider;
            const isDisconnecting = disconnectingProvider === provider;
            const providerError = providerErrors[provider];
            const readiness = providerReadiness[provider];
            const hasServerConfigIssue = readiness ? !readiness.readyForInit : false;
            const hasStatusWarnings = Boolean(readiness && readiness.warnings.length > 0);
            const waitingSeconds = isOAuthBusy ? Math.floor((oauthProgress?.elapsedMs ?? 0) / 1000) : 0;
            const authorizedAt = typeof integration?.config?.connected_at === 'string' &&
              !Number.isNaN(Date.parse(integration.config.connected_at))
              ? integration.config.connected_at
              : null;
            const missingEnvLabel = readiness && readiness.missingEnvVars.length > 0
              ? readiness.missingEnvVars.join(', ')
              : null;

            return (
              <Card key={provider} className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">{meta.label}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{meta.description}</p>
                  {integration?.display_name && (
                    <p className="text-xs text-gray-400 mt-0.5">{integration.display_name}</p>
                  )}
                  {provider === 'hubspot' && isConnected && integration?.config?.hubspot_user_level !== true && (
                    <p className="text-xs text-gray-500 mt-1">
                      Les entreprises HubSpot sont vos comptes Domino, visibles par toute l’équipe.
                      Domino ne reproduit pas les permissions HubSpot de chaque utilisateur.
                    </p>
                  )}
                  {provider === 'hubspot' && isConnected && integration?.config?.hubspot_user_level === true && (
                    <p className="text-xs text-amber-700 mt-1">
                      Accès utilisateur : vos entreprises HubSpot ne sont pas importées.
                    </p>
                  )}
                  {hasServerConfigIssue && (
                    <p className="text-xs text-amber-700 mt-1">
                      {missingEnvLabel
                        ? `Variables manquantes: ${missingEnvLabel}.`
                        : 'Configuration OAuth serveur invalide.'}{' '}
                      Vérifiez l'env des Edge Functions puis redémarrez `npx supabase functions serve --env-file .env`.
                    </p>
                  )}
                  {hasStatusWarnings && !hasServerConfigIssue && (
                    <p className="text-xs text-amber-700 mt-1">
                      {readiness?.warnings.join(' ')}
                    </p>
                  )}
                  {isConnected && hasScopeDrift && (
                    <p className="text-xs text-amber-700 mt-1">
                      Reconnexion requise pour ajouter les permissions: {formatScopes(missingScopes)}
                    </p>
                  )}
                  {integration && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {authorizedAt ? 'Dernière autorisation le ' : 'Connexion enregistrée le '}
                      {new Date(authorizedAt ?? integration.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  {isOAuthBusy && (
                    <p className="text-xs text-gray-500 mt-1">
                      {oauthProgress?.phase === 'starting'
                        ? 'Ouverture de la fenêtre d’autorisation...'
                        : `Autorisation en cours... (${waitingSeconds}s)`}
                    </p>
                  )}
                  {providerError && (
                    <p className="text-xs text-red-600 mt-1">{providerError}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleConnect(provider)}
                    disabled={Boolean(oauthProgress) || Boolean(disconnectingProvider) || hasServerConfigIssue}
                    loading={isOAuthBusy}
                  >
                    {isConnected ? 'Reconnecter' : 'Connecter'}
                  </Button>

                  {isConnected && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDisconnect(provider)}
                      disabled={Boolean(oauthProgress) || isDisconnecting}
                      loading={isDisconnecting}
                    >
                      Déconnecter
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-sm text-domino-inkSoft">
        Les documents de l’entreprise et vos dossiers personnels sont dans{' '}
        <Link to="/profil?tab=documents" className="text-domino-aubergine underline-offset-2 hover:underline">Profil → Documents</Link>.
      </p>
    </div>
  );
};
